import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import {
  DollarSign,
  CreditCard,
  TrendingUp,
  Calendar,
  ExternalLink,
  RefreshCw
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const Payments = () => {
  const { api } = useApi();
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPaymentData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch payment history
      const paymentsResponse = await api.get('/payments/history');
      setPayments(paymentsResponse.data.data.payments || []);

      // Calculate stats
      const payments = paymentsResponse.data.data.payments || [];
      const totalEarnings = payments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
      const pendingPayouts = payments.filter(p => p.status === 'pending').reduce((sum, p) => sum + (p.amount || 0), 0);
      const completedPayouts = payments.filter(p => p.status === 'completed').reduce((sum, p) => sum + (p.amount || 0), 0);

      setStats({
        totalEarnings,
        pendingPayouts,
        completedPayouts,
        totalPayments: payments.length
      });
    } catch (error) {
      console.error('Failed to fetch payment data:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchPaymentData();
  }, [fetchPaymentData]);

  const handleStripeDashboard = async () => {
    try {
      const response = await api.get('/payments/stripe-dashboard');
      window.open(response.data.data.url, '_blank');
    } catch (error) {
      console.error('Failed to get Stripe dashboard URL:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'text-warning-600 bg-warning-50 border-warning-200';
      case 'completed':
        return 'text-success-600 bg-success-50 border-success-200';
      case 'failed':
        return 'text-error-600 bg-error-50 border-error-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <LoadingSpinner size="xl" text="Loading payments..." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Payments</h1>
            <p className="text-gray-600">Track your earnings and manage payouts</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={fetchPaymentData}
              className="btn-outline"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </button>
            {user?.stripeAccountId && (
              <button
                onClick={handleStripeDashboard}
                className="btn-primary"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Stripe Dashboard
              </button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="card">
            <div className="flex items-center">
              <div className="bg-primary-100 p-3 rounded-lg">
                <DollarSign className="w-6 h-6 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Earnings</p>
                <p className="text-2xl font-bold text-gray-900">
                  ${(stats?.totalEarnings || 0).toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="bg-warning-100 p-3 rounded-lg">
                <Calendar className="w-6 h-6 text-warning-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pending Payouts</p>
                <p className="text-2xl font-bold text-gray-900">
                  ${(stats?.pendingPayouts || 0).toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="bg-success-100 p-3 rounded-lg">
                <TrendingUp className="w-6 h-6 text-success-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Completed Payouts</p>
                <p className="text-2xl font-bold text-gray-900">
                  ${(stats?.completedPayouts || 0).toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="bg-secondary-100 p-3 rounded-lg">
                <CreditCard className="w-6 h-6 text-secondary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Payouts</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats?.totalPayments || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Stripe Account Status */}
        {!user?.stripeAccountId && (
          <div className="card bg-warning-50 border-warning-200">
            <div className="flex items-center">
              <div className="bg-warning-100 p-3 rounded-lg">
                <CreditCard className="w-6 h-6 text-warning-600" />
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-medium text-warning-800">
                  Payment Setup Required
                </h3>
                <p className="text-warning-700">
                  Set up your Stripe account to start receiving payments from families.
                </p>
              </div>
              <button
                onClick={handleStripeDashboard}
                className="btn-warning"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Set Up Stripe
              </button>
            </div>
          </div>
        )}

        {/* Payment History */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Payment History</h2>
          </div>

          {payments.length === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No payments yet</h3>
              <p className="text-gray-500">
                Payments will appear here once you complete orders and receive payouts.
              </p>
            </div>
          ) : (
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reference
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {payments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(payment.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {payment.description}
                        </div>
                        {payment.orderId && (
                          <div className="text-sm text-gray-500">
                            Order #{payment.orderId.slice(-8)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        ${payment.amount?.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(payment.status)}`}>
                          {payment.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {payment.stripeTransferId ? (
                          <span className="font-mono text-xs">
                            {payment.stripeTransferId.slice(-8)}
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Payout Schedule Info */}
        <div className="card bg-blue-50 border-blue-200">
          <div className="flex items-start">
            <div className="bg-blue-100 p-3 rounded-lg">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-blue-800">
                Payout Schedule
              </h3>
              <p className="text-blue-700 mt-1">
                Payouts are processed automatically every Monday for earnings from the previous week. 
                Funds typically arrive in your bank account within 2-3 business days.
              </p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Payments; 